import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Search, Send, CheckCheck, Clock, AlertTriangle, ExternalLink, Trash2, X, Copy, ChevronDown, ChevronUp, FileText, BookOpen, ArrowRightCircle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from '@/components/RichTextEditor';
import IsolatedRichTextField from '@/components/IsolatedRichTextField';
import RichTextDisplay from '@/components/RichTextDisplay';
import { FileUpload } from '@/components/FileUpload';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { EntityStatusLogTab } from '@/components/EntityStatusLogTab';
import api from '@/lib/api';

const SUCCESS_OPTIONS = ['Pending', 'Not Awarded', 'Successful'];
const SUCCESS_COLORS = {
  'Pending': 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  'Not Awarded': 'bg-zinc-500/15 text-zinc-400 border-zinc-500/25',
  'Successful': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
};

function getDaysInfo(dueDate) {
  if (!dueDate) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00');
  const diff = Math.ceil((due - now) / 86400000);
  if (diff > 0) return { days: diff, label: `${diff} Day${diff !== 1 ? 's' : ''} Left`, color: diff <= 4 ? 'text-amber-400' : diff <= 10 ? 'text-blue-400' : 'text-muted-foreground', urgent: diff <= 4 };
  if (diff === 0) return { days: 0, label: 'Due Today', color: 'text-destructive', urgent: true };
  return { days: Math.abs(diff), label: `${Math.abs(diff)} Day${Math.abs(diff) !== 1 ? 's' : ''} Overdue`, color: 'text-destructive', urgent: true };
}

const STALE_DAYS = 14;
const URGENT_DUE_DAYS = 7;

function daysSince(ts) {
  if (!ts) return Infinity;
  const t = new Date(ts).getTime();
  if (!t) return Infinity;
  return (Date.now() - t) / 86400000;
}

function daysUntilDue(dueDate) {
  if (!dueDate) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00').getTime();
  return Math.ceil((due - now.getTime()) / 86400000);
}

/** Classify an RFP into one of: action, active, won, lost. `success_status` wins. */
function classifyRfp(r) {
  if (r.success_status === 'Successful') return 'won';
  if (r.success_status === 'Not Awarded') return 'lost';
  // Pending → split into Action Required vs Active Pipeline
  const due = daysUntilDue(r.due_date);
  const stale = daysSince(r.updated_at) >= STALE_DAYS;
  const notContacted = !r.reached_out;
  const dueSoon = due !== null && due <= URGENT_DUE_DAYS;
  if (notContacted || dueSoon || stale) return 'action';
  return 'active';
}

/** Build a human-readable next-action label + severity for a row. */
function nextActionFor(r) {
  if (r.success_status === 'Successful') {
    return r.proposal_id
      ? { label: 'Converted to Proposal', tone: 'ok' }
      : { label: 'Convert to Proposal', tone: 'warn' };
  }
  if (r.success_status === 'Not Awarded') {
    return { label: 'Archived. No action', tone: 'muted' };
  }
  const due = daysUntilDue(r.due_date);
  if (!r.reached_out) {
    if (due !== null && due <= URGENT_DUE_DAYS) {
      return { label: due < 0 ? 'Overdue: contact now' : 'Contact agency (due soon)', tone: 'danger' };
    }
    return { label: 'Contact agency', tone: 'warn' };
  }
  if (r.submitted_for_review && !r.email_approved) {
    return { label: 'Awaiting email approval', tone: 'warn' };
  }
  if (due !== null && due <= URGENT_DUE_DAYS) {
    return { label: due < 0 ? 'Overdue: follow up' : `Due in ${due}d`, tone: due < 0 ? 'danger' : 'warn' };
  }
  if (daysSince(r.updated_at) >= STALE_DAYS) {
    return { label: `Stale ${Math.floor(daysSince(r.updated_at))}d: follow up`, tone: 'warn' };
  }
  return { label: 'Awaiting response', tone: 'muted' };
}

const NEXT_ACTION_TONES = {
  danger: 'text-destructive',
  warn: 'text-amber-400',
  ok: 'text-emerald-400',
  muted: 'text-muted-foreground',
};

const EMPTY_FORM = {
  agency: '', bid_title: '', reached_out: false,
  contact_person: '', contact_phone: '', contact_email: '',
  response_details: '', follow_up_notes: '', success_status: 'Pending',
  additional_notes: '', overview_notes: '', primary_links: [''],
  publication_date: '', due_date: '', date_reached_out: '',
  email_draft: '', email_approved: false, document_ids: [],
};

export function RfpTrackerTab() {
  const [rfps, setRfps] = useState([]);
  const [search, setSearch] = useState('');
  const [filterContacted, setFilterContacted] = useState('all');
  const [filterSuccess, setFilterSuccess] = useState('all');
  const [bucket, setBucket] = useState('action'); // action | active | won | lost | all
  const [selected, setSelected] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingRfp, setEditingRfp] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  // Isolated ref for the Notes/Comments RichTextEditor - keeps keystrokes
  // out of `form` state so they don't re-render the entire 1000+ line tab.
  const overviewNotesRef = useRef(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
  const [bulkForms, setBulkForms] = useState([{ ...EMPTY_FORM }]);
  const [documents, setDocuments] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', agency_type: '', subject: '', body: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  const fetchRfps = useCallback(() => {
    api.get('/rfp-opportunities').then(r => setRfps(r.data || [])).catch(() => {});
  }, []);

  const fetchTemplates = useCallback(() => {
    api.get('/rfp-templates').then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchRfps();
    fetchTemplates();
    api.get('/users')
      .then(r => setUsers((r.data || []).filter(u => u.is_active !== false && u.role !== 'Client' && u.email)))
      .catch(() => setUsers([]));
  }, [fetchRfps, fetchTemplates]);

  // Fetch document info for displayed rfps
  useEffect(() => {
    const allDocIds = rfps.flatMap(r => r.document_ids || []);
    if (allDocIds.length === 0) return;
    const unique = [...new Set(allDocIds)];
    Promise.all(unique.map(id => api.get(`/files/${id}`).then(r => r.data).catch(() => null)))
      .then(results => {
        const map = {};
        results.filter(Boolean).forEach(d => { map[d.id] = d; });
        setDocuments(map);
      });
  }, [rfps]);

  // Classify every RFP once per data change - used for bucket counts and filtering.
  const classified = useMemo(() => rfps.map(r => ({ ...r, _bucket: classifyRfp(r) })), [rfps]);

  const bucketCounts = useMemo(() => {
    const c = { action: 0, active: 0, won: 0, lost: 0, all: classified.length };
    classified.forEach(r => { c[r._bucket] = (c[r._bucket] || 0) + 1; });
    return c;
  }, [classified]);

  // Sort: unapproved & not contacted first, then by due_date urgency
  const sortedRfps = useMemo(() => {
    let filtered = classified.filter(r => {
      if (bucket !== 'all' && r._bucket !== bucket) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.agency || '').toLowerCase().includes(s) &&
            !(r.bid_title || '').toLowerCase().includes(s) &&
            !(r.contact_person || '').toLowerCase().includes(s)) return false;
      }
      if (filterContacted === 'contacted' && !r.reached_out) return false;
      if (filterContacted === 'not_contacted' && r.reached_out) return false;
      if (filterSuccess !== 'all' && r.success_status !== filterSuccess) return false;
      return true;
    });

    return filtered.sort((a, b) => {
      // Priority 1: Not contacted & not approved first
      const aPriority = (!a.reached_out && !a.email_approved) ? 0 : (!a.reached_out ? 1 : 2);
      const bPriority = (!b.reached_out && !b.email_approved) ? 0 : (!b.reached_out ? 1 : 2);
      if (aPriority !== bPriority) return aPriority - bPriority;
      // Priority 2: Due date urgency (soonest first)
      const aDate = a.due_date || '9999-12-31';
      const bDate = b.due_date || '9999-12-31';
      return aDate.localeCompare(bDate);
    });
  }, [classified, bucket, search, filterContacted, filterSuccess]);

  const notContactedCount = rfps.filter(r => !r.reached_out && r.success_status !== 'Not Awarded').length;
  const pendingReviewCount = rfps.filter(r => r.submitted_for_review && !r.email_approved).length;

  const [detailTimeEntries, setDetailTimeEntries] = useState([]);
  const [detailStatusLog, setDetailStatusLog] = useState([]);

  const loadDetailExtras = (id) => {
    if (!id) {
      setDetailTimeEntries([]);
      setDetailStatusLog([]);
      return;
    }
    api.get(`/rfp-opportunities/${id}`)
      .then(r => {
        setDetailTimeEntries(r.data?.time_entries || []);
        setDetailStatusLog(r.data?.status_log || []);
      })
      .catch(() => {
        setDetailTimeEntries([]);
        setDetailStatusLog([]);
      });
  };

  const openDetail = (rfp) => {
    setEditingRfp(rfp);
    setForm({
      ...EMPTY_FORM,
      ...rfp,
      primary_links: (rfp.primary_links && rfp.primary_links.length > 0) ? rfp.primary_links : [''],
    });
    setDetailOpen(true);
    setCreateOpen(false);
    loadDetailExtras(rfp?.id);
  };

  const openCreate = () => {
    setEditingRfp(null);
    setForm({ ...EMPTY_FORM });
    setDetailOpen(true);
    setCreateOpen(true);
  };

  const openBulkCreate = () => {
    setBulkForms([{ ...EMPTY_FORM }]);
    setBulkCreateOpen(true);
  };

  const addBulkRow = () => {
    setBulkForms(prev => [...prev, { ...EMPTY_FORM }]);
  };

  const updateBulkRow = (idx, field, value) => {
    setBulkForms(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const removeBulkRow = (idx) => {
    setBulkForms(prev => prev.filter((_, i) => i !== idx));
  };

  const saveRecord = async () => {
    if (!form.bid_title?.trim()) { toast.error('Bid Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        primary_links: form.primary_links.filter(l => l.trim()),
        overview_notes: overviewNotesRef.current?.getValue() ?? form.overview_notes ?? '',
      };
      if (createOpen) {
        await api.post('/rfp-opportunities', payload);
        toast.success('RFP opportunity created');
      } else {
        await api.put(`/rfp-opportunities/${editingRfp.id}`, payload);
        toast.success('RFP opportunity updated');
      }
      setDetailOpen(false);
      fetchRfps();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Delete this RFP opportunity?')) return;
    try {
      await api.delete(`/rfp-opportunities/${id}`);
      toast.success('Deleted');
      setDetailOpen(false);
      fetchRfps();
    } catch { toast.error('Failed to delete'); }
  };

  const bulkCreate = async () => {
    const valid = bulkForms.filter(f => f.bid_title?.trim());
    if (valid.length === 0) { toast.error('At least one record needs a Bid Title'); return; }
    setSaving(true);
    try {
      const records = valid.map(f => ({ ...f, primary_links: (f.primary_links || []).filter(l => l.trim()) }));
      const res = await api.post('/rfp-opportunities/bulk-create', { records });
      toast.success(`Created ${res.data.created} RFP opportunit${res.data.created === 1 ? 'y' : 'ies'}`);
      setBulkCreateOpen(false);
      fetchRfps();
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  const bulkMarkContacted = async () => {
    if (selected.length === 0) { toast.error('Select records first'); return; }
    try {
      const res = await api.post('/rfp-opportunities/bulk-contact', {
        ids: selected,
        date_reached_out: new Date().toISOString().slice(0, 10)
      });
      toast.success(`Marked ${res.data.updated} as contacted`);
      setSelected([]);
      fetchRfps();
    } catch { toast.error('Failed to update'); }
  };

  const [reviewerPickerOpen, setReviewerPickerOpen] = useState(false);
  const [reviewerIds, setReviewerIds] = useState([]);
  const [sendingReview, setSendingReview] = useState(false);

  const openSubmitForReview = () => {
    if (selected.length === 0) { toast.error('Select records first'); return; }
    // Default to the user's idea of "managers" so the picker isn't empty.
    const defaults = (users || [])
      .filter(u => u.is_active !== false && u.email && (u.role === 'Owner' || u.role === 'Office Manager' || u.role === 'Administrator'))
      .map(u => u.id);
    setReviewerIds(defaults);
    setReviewerPickerOpen(true);
  };

  const submitForReview = async () => {
    if (sendingReview) return;
    if (selected.length === 0) { toast.error('Select records first'); return; }
    if (reviewerIds.length === 0) { toast.error('Pick at least one reviewer'); return; }
    setSendingReview(true);
    try {
      const res = await api.post('/rfp-opportunities/submit-for-review', {
        ids: selected,
        recipient_ids: reviewerIds,
      });
      toast.success(`Submitted ${res.data.submitted} for review. ${res.data.notified} reviewer${res.data.notified !== 1 ? 's' : ''} notified.`);
      setReviewerPickerOpen(false);
      setSelected([]);
      fetchRfps();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSendingReview(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === sortedRfps.length) setSelected([]);
    else setSelected(sortedRfps.map(r => r.id));
  };

  const addLink = () => setForm(f => ({ ...f, primary_links: [...f.primary_links, ''] }));
  const updateLink = (idx, val) => setForm(f => ({ ...f, primary_links: f.primary_links.map((l, i) => i === idx ? val : l) }));
  const removeLink = (idx) => setForm(f => ({ ...f, primary_links: f.primary_links.filter((_, i) => i !== idx) }));

  const handleFileUploaded = (fileData) => {
    setForm(f => ({ ...f, document_ids: [...f.document_ids, fileData.id] }));
    setDocuments(prev => ({ ...prev, [fileData.id]: fileData }));
  };

  const removeDocument = (docId) => {
    setForm(f => ({ ...f, document_ids: f.document_ids.filter(id => id !== docId) }));
  };

  const toggleExpand = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  // ── Template Library ──
  const applyTemplate = (template) => {
    setForm(f => ({ ...f, email_draft: template.body }));
    toast.success(`Template "${template.name}" applied to Email Draft`);
  };

  const saveAsTemplate = async () => {
    if (!form.email_draft?.trim()) { toast.error('Write an email draft first'); return; }
    const name = window.prompt('Template name:', `${form.agency || 'General'} Outreach`);
    if (!name) return;
    try {
      await api.post('/rfp-templates', {
        name,
        agency_type: form.agency || '',
        subject: form.bid_title || '',
        body: form.email_draft,
      });
      toast.success('Template saved');
      fetchTemplates();
    } catch { toast.error('Failed to save template'); }
  };

  const saveTemplate = async () => {
    if (!templateForm.name?.trim()) { toast.error('Template name required'); return; }
    try {
      if (editingTemplate) {
        await api.put(`/rfp-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/rfp-templates', templateForm);
        toast.success('Template created');
      }
      setEditingTemplate(null);
      setTemplateForm({ name: '', agency_type: '', subject: '', body: '' });
      fetchTemplates();
    } catch { toast.error('Failed to save template'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/rfp-templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const editTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, agency_type: t.agency_type || '', subject: t.subject || '', body: t.body || '' });
  };

  // ── Convert to Proposal ──
  const convertToProposal = async (rfpId) => {
    if (!window.confirm('Convert this RFP to a Proposal at Internal Review stage?')) return;
    try {
      const res = await api.post(`/rfp-opportunities/${rfpId}/convert-to-proposal`);
      toast.success(res.data.message);
      setDetailOpen(false);
      fetchRfps();
      // Navigate to proposals page
      navigate('/proposals', { state: { openProposalId: res.data.proposal.id } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to convert');
    }
  };

  return (
    <div className="space-y-4" data-testid="rfp-tracker-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive border-destructive/25">
            <AlertTriangle className="h-3 w-3 mr-1" />{notContactedCount} Not Contacted
          </Badge>
          {pendingReviewCount > 0 && (
            <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/25">
              <Clock className="h-3 w-3 mr-1" />{pendingReviewCount} Pending Review
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setTemplateLibraryOpen(true)} className="text-xs border-border" data-testid="rfp-template-library-btn">
            <BookOpen className="h-3 w-3 mr-1" /> Templates
          </Button>
          <Button size="sm" variant="outline" onClick={openBulkCreate} className="text-xs border-border" data-testid="rfp-bulk-create-btn">
            <Copy className="h-3 w-3 mr-1" /> Bulk Create
          </Button>
          <Button size="sm" onClick={openCreate} className="bg-primary text-primary-foreground text-xs" data-testid="rfp-create-btn">
            <Plus className="h-3 w-3 mr-1" /> New Opportunity
          </Button>
        </div>
      </div>

      {/* Bucket Tabs - status-bucketed pipeline view */}
      <TooltipProvider delayDuration={200}>
        <div className="flex items-center gap-1 border-b border-border overflow-x-auto" data-testid="rfp-bucket-tabs">
          {[
            { key: 'action', label: 'Action Required', testid: 'bucket-action', tone: 'text-destructive', dot: 'bg-destructive', tip: 'Pending RFPs that need your attention: not contacted, due within 7 days, or no activity for 14+ days.' },
            { key: 'active', label: 'Active Pipeline', testid: 'bucket-active', tone: 'text-blue-400', dot: 'bg-blue-400', tip: 'Pending RFPs cruising along: contacted, not urgent, not stale. Your in-flight watchlist.' },
            { key: 'won', label: 'Won', testid: 'bucket-won', tone: 'text-emerald-400', dot: 'bg-emerald-500', tip: 'RFPs marked Successful. Convert to a Proposal if not already done.' },
            { key: 'lost', label: 'Lost', testid: 'bucket-lost', tone: 'text-zinc-400', dot: 'bg-zinc-500', tip: 'RFPs marked Not Awarded. Kept for reference.' },
            { key: 'all', label: 'All', testid: 'bucket-all', tone: 'text-muted-foreground', dot: 'bg-muted-foreground', tip: 'Every RFP, unfiltered.' },
          ].map(t => {
            const active = bucket === t.key;
            return (
              <Tooltip key={t.key}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setBucket(t.key)}
                    className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${active ? `${t.tone} border-current` : 'text-muted-foreground border-transparent hover:text-foreground'}`}
                    data-testid={`rfp-${t.testid}-tab`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
                    {t.label}
                    <span className="text-xs opacity-70">({bucketCounts[t.key] || 0})</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px] text-xs">{t.tip}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Filters & Bulk Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search agency, title, bid ID..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border h-9 text-sm" data-testid="rfp-search" />
        </div>
        <Select value={filterContacted} onValueChange={setFilterContacted}>
          <SelectTrigger className="w-40 h-9 bg-card border-border text-xs" data-testid="rfp-filter-contacted"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Status</SelectItem>
            <SelectItem value="not_contacted" className="text-xs">Not Contacted</SelectItem>
            <SelectItem value="contacted" className="text-xs">Contacted</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSuccess} onValueChange={setFilterSuccess}>
          <SelectTrigger className="w-36 h-9 bg-card border-border text-xs" data-testid="rfp-filter-success"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Outcomes</SelectItem>
            {SUCCESS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {selected.length > 0 && (
          <div className="flex items-center gap-2 ml-2">
            <Badge className="text-xs bg-primary/10 text-primary">{selected.length} selected</Badge>
            <Button size="sm" variant="outline" className="text-xs h-8 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" onClick={bulkMarkContacted} data-testid="rfp-bulk-contact-btn">
              <CheckCheck className="h-3 w-3 mr-1" /> Mark Contacted
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-8 border-blue-500/30 text-blue-400 hover:bg-blue-500/10" onClick={openSubmitForReview} data-testid="rfp-submit-review-btn">
              <Send className="h-3 w-3 mr-1" /> Submit for Review
            </Button>
            <Button size="sm" variant="ghost" className="text-xs h-8 text-muted-foreground" onClick={() => setSelected([])}><X className="h-3 w-3" /></Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30 border-b border-border">
              <th className="p-2 w-8">
                <Checkbox checked={selected.length === sortedRfps.length && sortedRfps.length > 0} onCheckedChange={toggleSelectAll} data-testid="rfp-select-all" />
              </th>
              <th className="p-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
              <th className="p-2 text-left text-xs font-semibold text-muted-foreground">Agency</th>
              <th className="p-2 text-left text-xs font-semibold text-muted-foreground">Bid Title/ID</th>
              <th className="p-2 text-left text-xs font-semibold text-muted-foreground">Next Action</th>
              <th className="p-2 text-left text-xs font-semibold text-muted-foreground">Due Date</th>
              <th className="p-2 text-left text-xs font-semibold text-muted-foreground">Outcome</th>
              <th className="p-2 text-left text-xs font-semibold text-muted-foreground">Approved</th>
              <th className="p-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {sortedRfps.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-muted-foreground text-sm">No RFP opportunities match this view.</td></tr>
            ) : sortedRfps.map(rfp => {
              const daysInfo = getDaysInfo(rfp.due_date);
              const isExpanded = expandedRows[rfp.id];
              return (
                <TooltipProvider key={rfp.id} delayDuration={200}>
                  <tr
                    className={`border-b border-border hover:bg-muted/20 transition-colors cursor-pointer ${!rfp.reached_out ? 'bg-destructive/[0.03]' : ''}`}
                    onClick={() => openDetail(rfp)}
                    data-testid={`rfp-row-${rfp.id}`}
                  >
                    <td className="p-2" onClick={e => e.stopPropagation()}>
                      <Checkbox checked={selected.includes(rfp.id)} onCheckedChange={() => toggleSelect(rfp.id)} data-testid={`rfp-check-${rfp.id}`} />
                    </td>
                    <td className="p-2">
                      {rfp.reached_out ? (
                        <Badge className="text-xs bg-emerald-500/15 text-emerald-400 border-emerald-500/25">Contacted</Badge>
                      ) : (
                        <Badge className="text-xs bg-destructive/15 text-destructive border-destructive/25">Not Contacted</Badge>
                      )}
                    </td>
                    <td className="p-2 font-medium text-sm max-w-[140px] truncate">{rfp.agency || '-'}</td>
                    <td className="p-2 max-w-[260px]">
                      <p className="text-sm truncate font-medium">{rfp.bid_title || '-'}</p>
                    </td>
                    <td className="p-2 max-w-[200px]">
                      {(() => {
                        const na = nextActionFor(rfp);
                        return (
                          <span className={`text-xs font-medium ${NEXT_ACTION_TONES[na.tone]}`} data-testid={`rfp-next-action-${rfp.id}`}>
                            {na.tone === 'danger' && <AlertTriangle className="h-3 w-3 inline mr-1" />}
                            {na.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-col">
                        <span className="text-xs">{rfp.due_date || '-'}</span>
                        {daysInfo && (
                          <span className={`text-xs font-semibold ${daysInfo.color}`} data-testid={`rfp-days-${rfp.id}`}>
                            {daysInfo.urgent && <AlertTriangle className="h-3 w-3 inline mr-0.5" />}
                            {daysInfo.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      <Badge variant="outline" className={`text-xs ${SUCCESS_COLORS[rfp.success_status] || ''}`}>{rfp.success_status || 'Pending'}</Badge>
                    </td>
                    <td className="p-2">
                      {rfp.email_approved ? (
                        <Badge className="text-xs bg-emerald-500/15 text-emerald-400 border-emerald-500/25">Yes</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">No</Badge>
                      )}
                    </td>
                    <td className="p-2" onClick={e => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => toggleExpand(rfp.id)}>
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </Button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-muted/10">
                      <td colSpan={9} className="p-3">
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <p className="text-muted-foreground mb-1">Contact Info</p>
                            {rfp.contact_person && <p>{rfp.contact_person}</p>}
                            {rfp.contact_phone && <p>{rfp.contact_phone}</p>}
                            {rfp.contact_email && <p className="text-primary">{rfp.contact_email}</p>}
                            {!rfp.contact_person && !rfp.contact_phone && !rfp.contact_email && <p className="text-muted-foreground italic">No contact info</p>}
                          </div>
                          <div>
                            <p className="text-muted-foreground mb-1">Links</p>
                            {(rfp.primary_links || []).filter(Boolean).map((l, i) => (
                              <a key={i} href={l} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline truncate">
                                <ExternalLink className="h-3 w-3 shrink-0" />{l}
                              </a>
                            ))}
                            {!(rfp.primary_links || []).filter(Boolean).length && <p className="text-muted-foreground italic">No links</p>}
                          </div>
                          <div>
                            <p className="text-muted-foreground mb-1">Dates</p>
                            {rfp.publication_date && <p>Published: {rfp.publication_date}</p>}
                            {rfp.date_reached_out && <p>Reached Out: {rfp.date_reached_out}</p>}
                            {rfp.additional_notes && <p className="mt-1 text-muted-foreground">{rfp.additional_notes}</p>}
                          </div>
                        </div>
                        {(rfp.document_ids || []).length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground mb-1">Documents</p>
                            <div className="flex gap-1 flex-wrap">
                              {rfp.document_ids.map(docId => {
                                const doc = documents[docId];
                                return doc ? (
                                  <Badge key={docId} variant="outline" className="text-xs cursor-pointer hover:bg-muted/30" onClick={() => setPreviewDoc(doc)}>
                                    <FileText className="h-3 w-3 mr-1" />{doc.filename || 'Document'}
                                  </Badge>
                                ) : null;
                              })}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </TooltipProvider>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail / Create Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto bg-background border-border" data-testid="rfp-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-base font-heading">{createOpen ? 'New RFP Opportunity' : 'Edit RFP Opportunity'}</SheetTitle>
          </SheetHeader>
          <Tabs defaultValue="overview" className="mt-4">
            <TabsList className="h-auto">
              <TabsTrigger value="overview" className="text-xs" data-testid="rfp-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="status-log" className="text-xs" data-testid="rfp-tab-status-log">Status Log</TabsTrigger>
              <TabsTrigger value="docs" className="text-xs" data-testid="rfp-tab-docs">Documents</TabsTrigger>
              <TabsTrigger value="time-logged" className="text-xs" data-testid="rfp-tab-time-logged">Time Logged</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Agency *</Label>
                <Input value={form.agency} onChange={e => setForm({ ...form, agency: e.target.value })} className="bg-card border-border" data-testid="rfp-form-agency" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Bid Title/ID *</Label>
                <Input value={form.bid_title} onChange={e => setForm({ ...form, bid_title: e.target.value })} className="bg-card border-border" data-testid="rfp-form-bid-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Publication Date</Label>
                  <Input type="date" value={form.publication_date} onChange={e => setForm({ ...form, publication_date: e.target.value })} className="bg-card border-border" data-testid="rfp-form-pub-date" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-card border-border" data-testid="rfp-form-due-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Outcome</Label>
                <Select value={form.success_status} onValueChange={v => setForm({ ...form, success_status: v })}>
                  <SelectTrigger className="bg-card border-border" data-testid="rfp-form-success"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {SUCCESS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Primary Links</Label>
                <div className="space-y-1.5">
                  {form.primary_links.map((link, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <Input value={link} onChange={e => updateLink(i, e.target.value)} placeholder="https://..." className="bg-card border-border text-xs" data-testid={`rfp-form-link-${i}`} />
                      {form.primary_links.length > 1 && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={() => removeLink(i)}><X className="h-3 w-3" /></Button>
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="text-xs h-7 border-border" onClick={addLink}><Plus className="h-3 w-3 mr-1" /> Add Link</Button>
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Notes / Comments</Label>
                <IsolatedRichTextField
                  ref={overviewNotesRef}
                  initialValue={form.overview_notes || ''}
                  resetKey={editingRfp?.id || (createOpen ? 'new' : 'none')}
                  placeholder="Internal notes or comments about this RFP (scope nuances, internal pricing thoughts, who to loop in, prior history)..."
                  collapsedHeight={80}
                  data-testid="rfp-form-overview-notes"
                />
              </div>

              {!createOpen && editingRfp?.success_status === 'Successful' && !editingRfp?.proposal_id && (
                <div className="border border-emerald-500/20 rounded-lg p-3 bg-emerald-500/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-emerald-400">Successful Bid</p>
                      <p className="text-xs text-muted-foreground">Convert this RFP to a Proposal at Internal Review stage with Research task path.</p>
                    </div>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => convertToProposal(editingRfp.id)} data-testid="rfp-convert-proposal-btn">
                      <ArrowRightCircle className="h-3 w-3 mr-1" /> Convert to Proposal
                    </Button>
                  </div>
                </div>
              )}
              {!createOpen && editingRfp?.proposal_id && (
                <div className="border border-blue-500/20 rounded-lg p-3 bg-blue-500/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-blue-400">Proposal Created</p>
                      <p className="text-xs text-muted-foreground">{editingRfp.proposal_number || 'Linked to proposal'}</p>
                    </div>
                    <Button size="sm" variant="outline" className="text-xs border-blue-500/30 text-blue-400" onClick={() => navigate('/proposals')}>
                      View Proposals
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="docs" className="mt-4 space-y-3">
              <Label className="text-xs text-muted-foreground">Accompanying Documents</Label>
              {(form.document_ids || []).length > 0 && (
                <div className="flex gap-1.5 flex-wrap mb-2">
                  {form.document_ids.map(docId => {
                    const doc = documents[docId];
                    return (
                      <Badge key={docId} variant="outline" className="text-xs gap-1">
                        <FileText className="h-3 w-3" />
                        <span className="cursor-pointer hover:underline" onClick={() => doc && setPreviewDoc(doc)}>{doc?.filename || docId.slice(0, 8)}</span>
                        <X className="h-3 w-3 cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removeDocument(docId)} />
                      </Badge>
                    );
                  })}
                </div>
              )}
              <FileUpload onUploadComplete={handleFileUploaded} />
            </TabsContent>

            <TabsContent value="time-logged" className="mt-4 space-y-3" data-testid="rfp-time-logged-tab">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{detailTimeEntries.length} timesheet entries</p>
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  {detailTimeEntries.reduce((s, t) => s + (Number(t.hours) || 0), 0).toFixed(2)} total hours
                </Badge>
              </div>
              {detailTimeEntries.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No time logged against this RFP yet</p>
              ) : (
                <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
                  {detailTimeEntries.map(t => (
                    <div key={t.id} className="border border-border rounded-md bg-background p-3" data-testid={`rfp-time-entry-${t.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-muted-foreground">{t.date}</span>
                        <span className="text-sm font-bold text-primary">{t.hours}h</span>
                        <Badge className={`text-xs ${t.status === 'Approved' ? 'bg-emerald-500/10 text-emerald-400' : t.status === 'Submitted' ? 'bg-amber-500/10 text-amber-400' : t.status === 'Rejected' ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-muted-foreground'}`}>{t.status}</Badge>
                        {!t.is_billable && <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.user_name}</p>
                      {t.description && <div className="mt-1 text-xs text-foreground/80" dangerouslySetInnerHTML={{ __html: t.description }} />}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="status-log" className="mt-4" data-testid="rfp-status-log-tab-content">
              {editingRfp?.id && (
                <EntityStatusLogTab
                  entityType="rfp"
                  entityId={editingRfp.id}
                  entityLabel={[editingRfp?.agency, editingRfp?.bid_title].filter(Boolean).join(' - ')}
                  statusLog={detailStatusLog}
                  onChange={() => loadDetailExtras(editingRfp.id)}
                />
              )}
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-2 pt-3 mt-4 border-t border-border">
            <Button onClick={saveRecord} disabled={saving} className="bg-primary text-primary-foreground" data-testid="rfp-form-save">
              {saving ? 'Saving...' : createOpen ? 'Create Opportunity' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Cancel</Button>
            {!createOpen && editingRfp && (
              <Button variant="ghost" className="ml-auto text-destructive" onClick={() => deleteRecord(editingRfp.id)} data-testid="rfp-form-delete">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Bulk Create Sheet */}
      <Sheet open={bulkCreateOpen} onOpenChange={setBulkCreateOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto bg-background border-border" data-testid="rfp-bulk-create-sheet">
          <SheetHeader>
            <SheetTitle className="text-base font-heading">Bulk Create RFP Opportunities</SheetTitle>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <p className="text-xs text-muted-foreground">Add multiple RFP records at once. Fill in the core fields: you can edit additional details later.</p>
            {bulkForms.map((bf, idx) => (
              <Card key={idx} className="border-border bg-card/50 shadow-none">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-muted-foreground">Record {idx + 1}</p>
                    {bulkForms.length > 1 && (
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeBulkRow(idx)}><Trash2 className="h-3 w-3" /></Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Agency</Label>
                      <Input value={bf.agency} onChange={e => updateBulkRow(idx, 'agency', e.target.value)} className="bg-background border-border text-xs h-8" data-testid={`bulk-rfp-agency-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Bid Title/ID *</Label>
                      <Input value={bf.bid_title} onChange={e => updateBulkRow(idx, 'bid_title', e.target.value)} className="bg-background border-border text-xs h-8" data-testid={`bulk-rfp-title-${idx}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Due Date</Label>
                      <Input type="date" value={bf.due_date} onChange={e => updateBulkRow(idx, 'due_date', e.target.value)} className="bg-background border-border text-xs h-8" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Publication Date</Label>
                      <Input type="date" value={bf.publication_date} onChange={e => updateBulkRow(idx, 'publication_date', e.target.value)} className="bg-background border-border text-xs h-8" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Primary Links</Label>
                    {(bf.primary_links || ['']).map((link, li) => (
                      <div key={li} className="flex gap-1 mb-1">
                        <Input value={link} onChange={e => { const links = [...(bf.primary_links || [''])]; links[li] = e.target.value; updateBulkRow(idx, 'primary_links', links); }} placeholder="https://..." className="bg-background border-border text-xs h-7 flex-1" data-testid={`bulk-rfp-link-${idx}-${li}`} />
                        {(bf.primary_links || ['']).length > 1 && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => { const links = (bf.primary_links || ['']).filter((_, i) => i !== li); updateBulkRow(idx, 'primary_links', links); }}><X className="h-3 w-3" /></Button>}
                      </div>
                    ))}
                    <Button size="sm" variant="ghost" className="text-xs text-muted-foreground h-6 px-1" onClick={() => updateBulkRow(idx, 'primary_links', [...(bf.primary_links || ['']), ''])}><Plus className="h-3 w-3 mr-0.5" /> Add Link</Button>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Notes / Comments</Label>
                    <Textarea
                      value={bf.overview_notes || ''}
                      onChange={e => updateBulkRow(idx, 'overview_notes', e.target.value)}
                      placeholder="Scope nuances, internal pricing thoughts, who to loop in, prior history..."
                      rows={3}
                      className="bg-background border-border text-xs resize-y"
                      data-testid={`bulk-rfp-notes-${idx}`}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button size="sm" variant="outline" className="text-xs border-dashed border-border w-full" onClick={addBulkRow} data-testid="rfp-bulk-add-row">
              <Plus className="h-3 w-3 mr-1" /> Add Another Record
            </Button>
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button onClick={bulkCreate} disabled={saving} className="bg-primary text-primary-foreground" data-testid="rfp-bulk-submit">
                {saving ? 'Creating...' : `Create ${bulkForms.filter(f => f.bid_title?.trim()).length} Record${bulkForms.filter(f => f.bid_title?.trim()).length !== 1 ? 's' : ''}`}
              </Button>
              <Button variant="outline" onClick={() => setBulkCreateOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Document Preview */}
      <DocumentPreviewModal doc={previewDoc} open={!!previewDoc} onClose={() => setPreviewDoc(null)} />

      {/* Template Library Sheet */}
      <Sheet open={templateLibraryOpen} onOpenChange={setTemplateLibraryOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-background border-border" data-testid="rfp-template-library-sheet">
          <SheetHeader>
            <SheetTitle className="text-base font-heading">Email Template Library</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <p className="text-xs text-muted-foreground">Create and manage reusable email templates. Templates can be applied to any RFP&apos;s Email Draft field.</p>

            {/* Create/Edit Template Form */}
            <Card className="border-primary/20 border-dashed bg-primary/5 shadow-none">
              <CardContent className="p-3 space-y-2">
                <p className="text-xs font-semibold text-primary">{editingTemplate ? 'Edit Template' : 'New Template'}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Template Name *</Label>
                    <Input value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="e.g. NYC DDC Standard Outreach" className="bg-background border-border text-xs h-8" data-testid="template-form-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Agency Type</Label>
                    <Input value={templateForm.agency_type} onChange={e => setTemplateForm({ ...templateForm, agency_type: e.target.value })} placeholder="e.g. NYC DDC, NYS DOT..." className="bg-background border-border text-xs h-8" data-testid="template-form-agency" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Subject Line</Label>
                  <Input value={templateForm.subject} onChange={e => setTemplateForm({ ...templateForm, subject: e.target.value })} placeholder="Email subject" className="bg-background border-border text-xs h-8" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Email Body</Label>
                  <RichTextEditor value={templateForm.body} onChange={v => setTemplateForm({ ...templateForm, body: v })} placeholder="Compose your template email body..." collapsedHeight={100} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveTemplate} className="bg-primary text-primary-foreground text-xs" data-testid="template-form-save">
                    <Save className="h-3 w-3 mr-1" /> {editingTemplate ? 'Update' : 'Save Template'}
                  </Button>
                  {editingTemplate && (
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', agency_type: '', subject: '', body: '' }); }}>
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Template List */}
            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No templates yet. Create your first one above or use &quot;Save as Template&quot; when editing an RFP&apos;s email draft.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">{templates.length} Template{templates.length !== 1 ? 's' : ''}</p>
                {templates.map(t => (
                  <Card key={t.id} className="border-border bg-card/50 shadow-none" data-testid={`template-card-${t.id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <p className="text-sm font-medium">{t.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t.agency_type && <span className="mr-2">{t.agency_type}</span>}
                            {t.created_by_name && <span>by {t.created_by_name}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="text-xs h-7 border-border" onClick={() => editTemplate(t)}>Edit</Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteTemplate(t.id)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                      {t.body && (
                        <div className="text-xs text-muted-foreground border-t border-border pt-1.5 mt-1.5 max-h-20 overflow-hidden">
                          <RichTextDisplay content={t.body} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Submit-for-Review reviewer picker */}
      <Sheet open={reviewerPickerOpen} onOpenChange={setReviewerPickerOpen}>
        <SheetContent className="w-full sm:max-w-md" data-testid="rfp-reviewer-picker-sheet">
          <SheetHeader>
            <SheetTitle>Submit {selected.length} Record{selected.length !== 1 ? 's' : ''} for Review</SheetTitle>
          </SheetHeader>
          <div className="py-3 space-y-3">
            <p className="text-xs text-muted-foreground">
              Pick the team members who should be notified. The email pulled from each employee&apos;s record will be used.
            </p>
            <div className="flex items-center justify-between text-xs pb-2 border-b border-border">
              <span className="text-muted-foreground">{reviewerIds.length} of {users.length} selected</span>
              <div className="flex items-center gap-3">
                <button
                  className="text-primary hover:underline"
                  onClick={() => setReviewerIds(users.map(u => u.id))}
                  data-testid="rfp-reviewers-select-all"
                >Select all</button>
                <button
                  className="text-muted-foreground hover:underline"
                  onClick={() => setReviewerIds([])}
                  data-testid="rfp-reviewers-clear"
                >Clear</button>
              </div>
            </div>
            <div className="max-h-[55vh] overflow-y-auto -mx-1 pr-1" data-testid="rfp-reviewers-list">
              {users.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No active employees with email on file.</p>
              ) : users.map(u => {
                const checked = reviewerIds.includes(u.id);
                return (
                  <label
                    key={u.id}
                    className="flex items-center gap-3 px-3 py-2 rounded hover:bg-muted/30 cursor-pointer text-xs"
                    data-testid={`rfp-reviewer-row-${u.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        setReviewerIds(prev => v ? [...prev, u.id] : prev.filter(id => id !== u.id));
                      }}
                      data-testid={`rfp-reviewer-check-${u.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    {u.role && <Badge variant="outline" className="text-xs shrink-0">{u.role}</Badge>}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button size="sm" variant="ghost" onClick={() => setReviewerPickerOpen(false)} data-testid="rfp-reviewers-cancel">Cancel</Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={submitForReview}
                disabled={sendingReview || reviewerIds.length === 0}
                data-testid="rfp-reviewers-send"
              >
                <Send className="h-3 w-3 mr-1" />
                {sendingReview ? 'Sending…' : `Notify ${reviewerIds.length}`}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
